import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular/standalone';
import { catchError, EMPTY } from 'rxjs';

import { VersionApiService } from './version-api.service';
import { Platform } from '@shared/enums/platform.enum';
import { Version } from '@shared/models/version.model';
import {
  ForceUpdateModalComponent,
} from '@shared/components/force-update-modal/force-update-modal.component';
import { environment } from '@environment/environment';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {

  private readonly _versionApiService = inject(VersionApiService);
  private readonly _modalController = inject(ModalController);

  /**
   * Fire-and-forget version check. Subscribes internally; never throws to the
   * caller. Shows a non-dismissable modal when the running version is below the
   * API minimum for the current platform.
   */
  check(): void {
    this._versionApiService
        .get$()
        .pipe(catchError(() => EMPTY))
        .subscribe({
          next: (versions) => void this._handleVersions(versions),
        });
  }

  private async _handleVersions(versions: Version[] | null): Promise<void> {
    if (!versions) {
      return;
    }

    const platform = this._currentPlatform();

    if (!platform) {
      return;
    }

    const versionEntry = versions.find((entry) => entry.platform === platform);

    if (!versionEntry) {
      return;
    }

    if (!versionEntry.isOutdated(environment.appVersion)) {
      return;
    }

    await this._presentForceUpdateModal(platform);
  }

  private _currentPlatform(): Platform | null {
    const nativePlatform = Capacitor.getPlatform();

    if (nativePlatform === 'android') {
      return Platform.ANDROID;
    }

    if (nativePlatform === 'ios') {
      return Platform.IOS;
    }

    return null;
  }

  private async _presentForceUpdateModal(platform: Platform): Promise<void> {
    const modal = await this._modalController.create({
      component: ForceUpdateModalComponent,
      componentProps: { platform },
      backdropDismiss: false,
      canDismiss: false,
    });

    await modal.present();
  }
}
